import { InAppReview } from "@capacitor-community/in-app-review";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import SettingsService from "../services/settings.service";
import TricksService from "../services/tricks.service";
import { getAdjustedTiming, QueryTiming } from "./useQueryConfig";

interface UseRateAppOptions {
    /** The logged-in user's ID - required for auto-prompting based on trick count */
    userId?: string;
    /** Minimum number of tricks user must have before prompting (default: 2) */
    minTrickCount?: number;
    /** Whether to automatically prompt when conditions are met */
    autoPrompt?: boolean;
    /** Callback when rating prompt is shown */
    onPromptShown?: () => void;
    /** Callback when user is redirected to app store */
    onAppStoreOpened?: () => void;
}

interface UseRateAppReturn {
    /** Manually trigger the rate app flow */
    promptForRating: () => Promise<void>;
    /** Check if user is eligible to be prompted (never prompted before) */
    isEligibleForFirstPrompt: boolean;
    /** Whether the eligibility check is loading */
    isLoading: boolean;
    /** Open the app store directly (bypass in-app review logic) */
    openAppStore: () => Promise<void>;
    /** Whether auto-prompt has been triggered */
    hasAutoPrompted: boolean;
}

const IOS_PROMPT_COOLDOWN_DAYS = 121;
const ANDROID_PROMPT_COOLDOWN_DAYS = 31;

/**
 * Hook to handle in-app review prompts with platform-specific cooldown logic.
 * 
 * iOS: Can only prompt every 121 days (Apple's limit is ~3 times per year)
 * Android: Can only prompt every 31 days
 * 
 * Usage on TrickVideo page (auto-prompt after user uploads their second trick):
 * ```tsx
 * // This will auto-prompt when the logged-in user has 2+ tricks and has never been prompted
 * const { isEligibleForFirstPrompt, hasAutoPrompted } = useRateApp({
 *   userId: state.user.id,
 *   minTrickCount: 2,
 *   autoPrompt: true
 * });
 * ```
 * 
 * Usage on Settings page (manual trigger):
 * ```tsx
 * const { promptForRating } = useRateApp();
 * 
 * const handleRateClick = async () => {
 *   await promptForRating();
 * };
 * ```
 */
export const useRateApp = (options: UseRateAppOptions = {}): UseRateAppReturn => {
    const {
        userId,
        minTrickCount = 2,
        autoPrompt = false,
        onPromptShown,
        onAppStoreOpened,
    } = options;

    const [isEligibleForFirstPrompt, setIsEligibleForFirstPrompt] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hasAutoPrompted, setHasAutoPrompted] = useState(false);
    const hasTriggeredAutoPrompt = useRef(false);

    const isNative = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();

    // Fetch user's tricks if userId is provided and autoPrompt is enabled
    const { data: userTricks, isLoading: isLoadingTricks } = useQuery({
        queryKey: ['userTricks', userId],
        queryFn: () => TricksService.getUserTricks(userId),
        enabled: !!userId && autoPrompt && isNative,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
    });

    // Check eligibility on mount
    useEffect(() => {
        const checkEligibility = async () => {
            if (!isNative) {
                setIsLoading(false);
                return;
            }

            try {
                const timeSinceLastPrompt = await SettingsService.getTimeSinceLastPrompt();
                // User has never been prompted before
                setIsEligibleForFirstPrompt(timeSinceLastPrompt === null);
            } catch (error) {
                console.error("Error checking rating eligibility:", error);
                setIsEligibleForFirstPrompt(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkEligibility();
    }, [isNative]);

    const calcDaysSinceLastPrompt = (timeSinceLastPrompt: string): number => {
        const dateSinceLastPrompt = new Date(timeSinceLastPrompt);
        const currentDate = new Date();
        const timeDiff = currentDate.getTime() - dateSinceLastPrompt.getTime();
        const daysSinceLastPrompt = timeDiff / (1000 * 3600 * 24);
        return daysSinceLastPrompt;
    };

    const openAppStore = useCallback(async () => {
        if (platform === 'ios') {
            await Browser.open({ url: 'https://apps.apple.com/us/app/ecliptic-ski-snowboard-app/id1607046376' });
        } else if (platform === 'android') {
            await Browser.open({ url: 'https://play.google.com/store/apps/details?id=xyz.infinitedegrees.ride' });
        } else {
            // Fallback for web or unknown platforms
            await Browser.open({ url: 'https://instagram.com/ecliptic.day' });
        }
        onAppStoreOpened?.();
    }, [platform, onAppStoreOpened]);

    const promptForRating = useCallback(async () => {
        console.log("useRateApp: promptForRating called");

        if (!isNative) {
            // For web, we can't show in-app review - caller should handle this case
            console.log("useRateApp: Not a native platform, skipping");
            return;
        }

        try {
            const timeSinceLastPrompt = await SettingsService.getTimeSinceLastPrompt();

            if (timeSinceLastPrompt === null) {
                // First time prompting - always show
                await SettingsService.setLastPromptTime();
                await InAppReview.requestReview();
                setIsEligibleForFirstPrompt(false);
                onPromptShown?.();
                console.log("useRateApp: First-time rating prompt shown");
            } else if (platform === 'ios') {
                const daysSinceLastPrompt = calcDaysSinceLastPrompt(timeSinceLastPrompt);
                if (daysSinceLastPrompt > IOS_PROMPT_COOLDOWN_DAYS) {
                    await SettingsService.setLastPromptTime();
                    await InAppReview.requestReview();
                    onPromptShown?.();
                    console.log("useRateApp: iOS rating prompt shown (cooldown passed)");
                } else {
                    await openAppStore();
                    console.log("useRateApp: iOS cooldown not passed, opening app store");
                }
            } else if (platform === 'android') {
                const daysSinceLastPrompt = calcDaysSinceLastPrompt(timeSinceLastPrompt);
                if (daysSinceLastPrompt > ANDROID_PROMPT_COOLDOWN_DAYS) {
                    await SettingsService.setLastPromptTime();
                    await InAppReview.requestReview();
                    onPromptShown?.();
                    console.log("useRateApp: Android rating prompt shown (cooldown passed)");
                } else {
                    await openAppStore();
                    console.log("useRateApp: Android cooldown not passed, opening app store");
                }
            } else {
                await openAppStore();
            }
        } catch (error) {
            console.error("useRateApp: Error prompting for rating:", error);
            // Fallback to opening app store on error
            await openAppStore();
        }
    }, [isNative, platform, openAppStore, onPromptShown]);

    // Auto-prompt logic: trigger when user has enough tricks and hasn't been prompted
    useEffect(() => {
        const triggerAutoPrompt = async () => {
            // Guard conditions
            if (!autoPrompt) return;
            if (!isNative) return;
            if (isLoading || isLoadingTricks) return;
            if (hasTriggeredAutoPrompt.current) return;
            if (!isEligibleForFirstPrompt) return;

            const trickCount = userTricks?.length ?? 0;
            if (trickCount < minTrickCount) return;

            // All conditions met - trigger the prompt
            console.log(`useRateApp: Auto-prompting (${trickCount} tricks >= ${minTrickCount} required)`);
            hasTriggeredAutoPrompt.current = true;
            setHasAutoPrompted(true);

            // Small delay to ensure the page has fully loaded
            await new Promise(resolve => setTimeout(resolve, 1500));
            await promptForRating();
        };

        triggerAutoPrompt();
    }, [
        autoPrompt,
        isNative,
        isLoading,
        isLoadingTricks,
        isEligibleForFirstPrompt,
        userTricks,
        minTrickCount,
        promptForRating
    ]);

    return {
        promptForRating,
        isEligibleForFirstPrompt,
        isLoading: isLoading || (autoPrompt && isLoadingTricks),
        openAppStore,
        hasAutoPrompted,
    };
};

export default useRateApp;
